/* ==================================================================== *
 * HS CART SMOOTH  —  Kopstorm
 * --------------------------------------------------------------------
 * Removes the cart-drawer flicker on add / quantity-change / remove.
 *
 * WHY THIS EXISTS
 * The theme refreshed the drawer in two ways, both of which tore the
 * DOM down and rebuilt it from server HTML:
 *
 *   1. cart-drawer._onCartRefresh()  (add to cart, every upsell,
 *      every subscription selector)  ->  this.replaceChildren(...)
 *      = the whole drawer is wiped and re-rendered. Line items vanish
 *        for a frame and every image reloads.  ("items disappear")
 *
 *   2. cart-drawer._onCartChanged()  (the +/- quantity buttons)
 *      ->  currentInner.innerHTML = updatedInner.innerHTML, fired on a
 *      1250ms timeout. = a second, delayed full repaint a beat after
 *        the click.
 *
 *   3. An inline Intelligems <script> in cart-drawer.liquid ran a
 *      MutationObserver that rewrote price innerHTML 200ms after EVERY
 *      mutation, adding a third repaint where the prices "popped in".
 *
 * THE FIX
 * Every mutation in the theme funnels through methods (1) and (2) on
 * the single <cart-drawer> custom element, so we override just those
 * two. Instead of replacing the DOM we MORPH it: we diff the fresh
 * server HTML against the live DOM and patch only the nodes that
 * actually changed. Existing line items (and their already-decoded
 * <img> nodes) are kept in place, so nothing disappears and nothing
 * reloads. Intelligems prices are applied once, synchronously, inside
 * the same morph pass (no observer, no 200ms pop).
 *
 * Self-contained, no dependencies, IIFE-wrapped.
 * ==================================================================== */
(function () {
  'use strict';

  if (window.__hsCartSmooth) return;
  window.__hsCartSmooth = true;

  /* ------------------------------------------------------------------ *
   * 1. MORPH ENGINE
   * ------------------------------------------------------------------ */

  var PRESERVE_ATTR = 'data-hs-preserve'; // subtree the theme controls itself
  var KEY_ATTR = 'data-hs-key';

  // Give every <line-item> a stable key derived from its Shopify line key
  // so increments / reorders / inserts patch in place instead of by index.
  function stampKeys(root) {
    if (!root || !root.querySelectorAll) return;
    var items = root.querySelectorAll('line-item');
    for (var i = 0; i < items.length; i++) {
      var li = items[i];
      if (li.getAttribute(KEY_ATTR)) continue;
      var input = li.querySelector('.quantity-input[data-line-key]');
      if (input) {
        li.setAttribute(KEY_ATTR, 'li:' + input.getAttribute('data-line-key'));
        continue;
      }
      // gift / e-book lines have no quantity control: fall back to title or image
      var title = li.querySelector('.line-item-title');
      var img = li.querySelector('img');
      var fallback =
        (title && title.textContent.trim()) ||
        (img && (img.getAttribute('src') || '')) ||
        'gift';
      li.setAttribute(KEY_ATTR, 'li:' + fallback);
    }
  }

  function keyOf(node) {
    if (!node || node.nodeType !== 1) return null;
    return node.getAttribute(KEY_ATTR) || node.id || null;
  }

  function isWhitespace(node) {
    return node.nodeType === 3 && !/\S/.test(node.nodeValue);
  }

  // Copy attributes from `to` onto `from`, removing any that disappeared.
  function syncAttributes(from, to) {
    // Add / update
    var toAttrs = to.attributes;
    for (var i = 0; i < toAttrs.length; i++) {
      var a = toAttrs[i];
      if (from.getAttribute(a.name) !== a.value) {
        from.setAttribute(a.name, a.value);
      }
    }
    // Remove
    var fromAttrs = from.attributes;
    for (var j = fromAttrs.length - 1; j >= 0; j--) {
      var name = fromAttrs[j].name;
      // never let a transient marker get stripped
      if (name === KEY_ATTR) continue;
      if (!to.hasAttribute(name)) from.removeAttribute(name);
    }
  }

  function morph(from, to) {
    // Different node kinds -> hard replace
    if (from.nodeType !== to.nodeType || from.nodeName !== to.nodeName) {
      from.replaceWith(to.cloneNode(true));
      return;
    }

    // Text / comment -> patch value only
    if (from.nodeType === 3 || from.nodeType === 8) {
      if (from.nodeValue !== to.nodeValue) from.nodeValue = to.nodeValue;
      return;
    }

    if (from.nodeType !== 1) return;

    // IMG: never reassign an unchanged src (that is what causes the
    // blank-then-reload flash). Only sync the other attributes.
    if (from.nodeName === 'IMG') {
      var fromSrc = from.getAttribute('src');
      var toSrc = to.getAttribute('src');
      syncAttributes(from, to);
      if (fromSrc && toSrc && fromSrc === toSrc) {
        from.setAttribute('src', fromSrc); // keep the live, decoded image
      }
      return;
    }

    // The free-shipping bar message ("You're €X away…") is rendered by the
    // element's own JS — the server outputs an empty placeholder (&nbsp;).
    // Leave this span untouched so the morph never wipes the live message.
    if (from.classList && from.classList.contains('bar-content')) {
      return;
    }

    // <pill-loader> builds its dots + checkmark in its own connectedCallback
    // and the server renders it EMPTY. If the morph diffed it against that
    // empty server node it would strip the dots (leaving a bare white pill),
    // and the element's show/hide animation — which targets its first child —
    // would then throw against the missing node. Leave it entirely to the
    // element. New line items get a fresh, upgraded pill-loader on insert.
    if (from.nodeName === 'PILL-LOADER') {
      return;
    }

    // <free-shipping-bar> renders its own message / fill / truck in response
    // to a `total-price` change (it observes that attribute). Sync ONLY
    // total-price so it re-renders itself; do NOT touch its threshold /
    // message attributes (already rate-adjusted on the live element) or its
    // class (it manages its own animation state). Its gift-unlock badge and
    // threshold label still get morphed from the server via the children.
    if (from.nodeName === 'FREE-SHIPPING-BAR') {
      var tp = to.getAttribute('total-price');
      if (tp != null && from.getAttribute('total-price') !== tp) {
        from.setAttribute('total-price', tp);
      }
      morphChildren(from, to);
      return;
    }

    // Subtrees the theme owns (carousels, Alpine widgets): sync the host
    // attributes but leave their children untouched.
    if (from.hasAttribute && from.hasAttribute(PRESERVE_ATTR)) {
      syncAttributes(from, to);
      return;
    }

    syncAttributes(from, to);

    // Preserve a focused quantity input the shopper is typing in: do not
    // clobber its value mid-edit (the server value reconciles on blur).
    if (
      from.nodeName === 'INPUT' &&
      document.activeElement === from &&
      from.classList.contains('quantity-input')
    ) {
      return;
    }
    if (from.nodeName === 'INPUT') {
      if (from.getAttribute('value') !== null) {
        // keep DOM property in sync with the (already synced) attribute
        if (to.hasAttribute('value')) from.value = to.getAttribute('value');
      }
    }

    morphChildren(from, to);
  }

  function morphChildren(fromParent, toParent) {
    // Index existing keyed children for O(1) lookup + move.
    var fromKeyed = Object.create(null);
    var n;
    for (n = fromParent.firstChild; n; n = n.nextSibling) {
      var fk = keyOf(n);
      if (fk) fromKeyed[fk] = n;
    }

    // Collect the target key set so we know what to delete afterwards.
    var toKeys = Object.create(null);
    for (n = toParent.firstChild; n; n = n.nextSibling) {
      var tk = keyOf(n);
      if (tk) toKeys[tk] = true;
    }

    var cursor = fromParent.firstChild;
    var toChild = toParent.firstChild;

    while (toChild) {
      var nextTo = toChild.nextSibling;
      var key = keyOf(toChild);
      var match = null;

      if (key && fromKeyed[key]) {
        match = fromKeyed[key];
      } else if (!key) {
        // Unkeyed: walk the cursor to the next unkeyed node of same kind,
        // skipping live keyed nodes (they belong to other slots) so we
        // never accidentally pair a keyed item with an unkeyed one.
        var probe = cursor;
        while (probe && keyOf(probe)) probe = probe.nextSibling;
        if (
          probe &&
          probe.nodeType === toChild.nodeType &&
          probe.nodeName === toChild.nodeName
        ) {
          match = probe;
        }
      }

      if (match) {
        if (match !== cursor) {
          fromParent.insertBefore(match, cursor);
        }
        cursor = match.nextSibling;
        morph(match, toChild);
      } else {
        fromParent.insertBefore(toChild.cloneNode(true), cursor);
      }

      toChild = nextTo;
    }

    // Remove any leftover live children that are gone from the target.
    var leftover = [];
    for (n = fromParent.firstChild; n; n = n.nextSibling) {
      var lk = keyOf(n);
      if (lk) {
        if (!toKeys[lk]) leftover.push(n);
      } else if (cursorPassed(fromParent, n, toParent)) {
        // trailing unkeyed extra beyond the new content
        if (!isWhitespace(n)) leftover.push(n);
        else leftover.push(n);
      }
    }
    for (var i = 0; i < leftover.length; i++) leftover[i].remove();
  }

  // True if `node` has no corresponding node in the new parent (used only
  // to trim trailing unkeyed extras). Conservative: counts unkeyed slots.
  function cursorPassed(fromParent, node, toParent) {
    // Count unkeyed children of the same nodeName up to `node`.
    var idx = 0;
    for (var c = fromParent.firstChild; c && c !== node; c = c.nextSibling) {
      if (!keyOf(c) && c.nodeName === node.nodeName) idx++;
    }
    var avail = 0;
    for (var t = toParent.firstChild; t; t = t.nextSibling) {
      if (!keyOf(t) && t.nodeName === node.nodeName) avail++;
    }
    return idx >= avail;
  }

  /* ------------------------------------------------------------------ *
   * 2. INTELLIGEMS PRICE APPLICATION (synchronous, no observer)
   *    Mirrors the original inline script's math but runs once per morph
   *    instead of on a 200ms MutationObserver timeout.
   * ------------------------------------------------------------------ */
  function applyIgPrices(root) {
    root = root || document;
    var ig = window.igData;
    if (!ig || !ig.price || !ig.price.getComparePriceByVariantId) return;

    var fmt = function (cents) {
      return '$' + (cents / 100).toFixed(2).replace(/\.00$/, '');
    };

    var totalCompare = 0;
    var hasIg = false;

    root.querySelectorAll('[data-ig-line-compare]').forEach(function (el) {
      var vid = el.getAttribute('data-variant-id');
      var qty = parseInt(el.getAttribute('data-qty'), 10) || 1;
      var cp = ig.price.getComparePriceByVariantId(vid);
      if (!cp) return;
      hasIg = true;
      var cpCents = Math.round(parseFloat(cp) * 100);
      var lineTotal = cpCents * qty;
      var sr = el.querySelector('.sr-only');
      var next = (sr ? sr.outerHTML + '\n' : '') + fmt(lineTotal);
      if (el.innerHTML !== next) el.innerHTML = next; // guard: avoid needless writes
      totalCompare += lineTotal;
    });

    root.querySelectorAll('[data-ig-line-savings]').forEach(function (el) {
      var vid = el.getAttribute('data-variant-id');
      var qty = parseInt(el.getAttribute('data-qty'), 10) || 1;
      var salePrice = parseInt(el.getAttribute('data-sale-price'), 10) || 0;
      var cp = ig.price.getComparePriceByVariantId(vid);
      if (!cp) return;
      var cpCents = Math.round(parseFloat(cp) * 100);
      var savings = (cpCents - salePrice) * qty;
      var next = fmt(savings) + ' savings';
      if (el.textContent !== next) el.textContent = next;
    });

    if (hasIg) {
      var discountEl = document.querySelector('[data-ig-cart-discount]');
      if (discountEl && totalCompare > 0 && window.__hsCartTotal != null) {
        var next = '- ' + fmt(totalCompare - window.__hsCartTotal);
        if (discountEl.textContent !== next) discountEl.textContent = next;
      }
    }
  }
  // Exposed so the (now observer-free) inline snippet can defer to it.
  window.__hsApplyIgPrices = applyIgPrices;

  /* ------------------------------------------------------------------ *
   * 3. SMOOTH UPDATE  —  morph the live drawer to fresh server HTML
   * ------------------------------------------------------------------ */
  // Clone the children of `fresh` into `live` as a clean, always-valid
  // fallback when a morph cannot be completed for any reason.
  function hardReplaceChildren(live, fresh) {
    var nodes = [];
    for (var n = fresh.firstChild; n; n = n.nextSibling) nodes.push(n.cloneNode(true));
    live.replaceChildren.apply(live, nodes);
  }

  function smoothUpdate(drawerEl, freshDrawerEl) {
    if (!drawerEl || !freshDrawerEl) return false;

    var liveInner = drawerEl.querySelector('.cart-drawer__inner');
    var freshInner = freshDrawerEl.querySelector('.cart-drawer__inner');

    // Empty <-> non-empty transition: structure differs entirely, so a
    // clean fade is nicer (and safer) than morphing across the boundary.
    if (!liveInner || !freshInner) {
      hardReplaceChildren(drawerEl, freshDrawerEl);
      stampKeys(drawerEl);
      applyIgPrices(drawerEl);
      return true;
    }

    stampKeys(liveInner);
    stampKeys(freshInner);

    // Body — morph in place; on ANY error fall back to a clean replace so the
    // drawer is never left in a half-built (frozen) state.
    try {
      morph(liveInner, freshInner);
    } catch (e) {
      hardReplaceChildren(liveInner, freshInner);
    }

    // Footer lives in a sibling [slot="footer"] node, not inside __inner.
    var liveFooter = drawerEl.querySelector('[slot="footer"]');
    var freshFooter = freshDrawerEl.querySelector('[slot="footer"]');
    if (liveFooter && freshFooter) {
      try {
        morph(liveFooter, freshFooter);
      } catch (e) {
        hardReplaceChildren(liveFooter, freshFooter);
      }
    } else if (freshFooter && !liveFooter) {
      drawerEl.appendChild(freshFooter.cloneNode(true));
    } else if (liveFooter && !freshFooter) {
      liveFooter.remove();
    }

    try {
      applyIgPrices(drawerEl);
    } catch (e) {}
    return true;
  }
  window.__hsSmoothUpdate = smoothUpdate;

  /* ------------------------------------------------------------------ *
   * 4. OVERRIDE THE TWO CHOKEPOINTS ON <cart-drawer>
   * ------------------------------------------------------------------ */
  function fetchSectionId(el) {
    var section = el.classList.contains('shopify-section')
      ? el
      : el.closest('.shopify-section');
    return section ? section.id.replace('shopify-section-', '') : null;
  }

  function patchPrototype(proto) {
    if (!proto || proto.__hsPatched) return;
    proto.__hsPatched = true;

    // (a) Add to cart / upsell / subscription  ->  morph instead of wipe.
    proto._onCartRefresh = async function () {
      try {
        var sectionId = fetchSectionId(this);
        var url =
          (window.Shopify && window.Shopify.routes && window.Shopify.routes.root
            ? window.Shopify.routes.root
            : '/') +
          '?section_id=' +
          sectionId;
        // Stale-guard: if a newer cart update lands while this fetch is in
        // flight (e.g. the shopper changes a quantity), drop this result so
        // it cannot overwrite the newer state.
        var myToken = (this.__hsToken = (this.__hsToken || 0) + 1);

        // Fetch the section HTML and the cart total in parallel; the total
        // keeps the Intelligems discount line accurate after an add.
        var results = await Promise.all([
          fetch(url).then(function (r) { return r.text(); }),
          fetch((window.Shopify && window.Shopify.routes && window.Shopify.routes.root
            ? window.Shopify.routes.root : '/') + 'cart.js')
            .then(function (r) { return r.json(); })
            .catch(function () { return null; }),
        ]);
        if (this.__hsToken !== myToken) return; // superseded by a newer update
        var html = results[0];
        if (results[1] && results[1].total_price != null) {
          window.__hsCartTotal = results[1].total_price;
        }
        var temp = document.createElement('div');
        temp.innerHTML = html;
        var fresh = temp.querySelector('#cart-drawer');
        if (!fresh) return;
        smoothUpdate(this, fresh);
      } catch (e) {
        // Fail safe: fall back to a hard refresh so the cart is never wrong.
        try {
          var sid = fetchSectionId(this);
          var u =
            (window.Shopify && window.Shopify.routes && window.Shopify.routes.root
              ? window.Shopify.routes.root
              : '/') +
            '?section_id=' +
            sid;
          var t2 = document.createElement('div');
          t2.innerHTML = await (await fetch(u)).text();
          var f2 = t2.querySelector('#cart-drawer');
          if (f2) this.replaceChildren.apply(this, Array.prototype.slice.call(f2.children));
        } catch (_) {}
      }
    };

    // (b) Quantity +/-  ->  morph immediately (no 1250ms delayed repaint).
    proto._onCartChanged = function (event) {
      try {
        var detail = event && event.detail;
        if (!detail || !detail.cart) return;
        // Supersede any in-flight async refresh with this fresher state.
        this.__hsToken = (this.__hsToken || 0) + 1;
        window.__hsCartTotal =
          detail.cart.total_price != null ? detail.cart.total_price : window.__hsCartTotal;

        var sectionId = fetchSectionId(this);
        var sectionHtml = detail.cart.sections && detail.cart.sections[sectionId];

        if (!sectionHtml) {
          // No bundled section came back; do a clean async morph instead.
          if (typeof this._onCartRefresh === 'function') this._onCartRefresh();
          return;
        }

        var doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
        var fresh = doc.querySelector('#cart-drawer') || doc.querySelector('.cart-drawer');
        if (!fresh) return;
        smoothUpdate(this, fresh);
      } catch (e) {
        if (typeof this._onCartRefresh === 'function') this._onCartRefresh();
      }
    };
  }

  // The theme binds its listeners in the constructor:
  //   this._onCartChangedListener = this._onCartChanged.bind(this)
  // so an element constructed BEFORE we patch the prototype keeps pointing
  // at the original methods. Re-bind those two listeners on live instances.
  function rewireInstance(el) {
    if (!el || el.__hsRewired) return;
    var proto = Object.getPrototypeOf(el);
    if (!proto || !proto.__hsPatched) return;
    // Only rewire once the element is actually constructed (listeners exist).
    if (typeof el._onCartChangedListener === 'undefined' &&
        typeof el._onCartRefreshListener === 'undefined') {
      return;
    }
    el.__hsRewired = true;

    if (el._onCartChangedListener) {
      document.removeEventListener('cart:change', el._onCartChangedListener);
    }
    el._onCartChangedListener = proto._onCartChanged.bind(el);
    document.addEventListener('cart:change', el._onCartChangedListener);

    if (el._onCartRefreshListener) {
      document.removeEventListener('cart:refresh', el._onCartRefreshListener);
    }
    el._onCartRefreshListener = proto._onCartRefresh.bind(el);
    document.addEventListener('cart:refresh', el._onCartRefreshListener);
  }

  function tryPatch() {
    var patched = false;
    var ctor = window.customElements && window.customElements.get('cart-drawer');
    if (ctor) {
      patchPrototype(ctor.prototype);
      patched = true;
    }
    var els = document.querySelectorAll('cart-drawer');
    for (var i = 0; i < els.length; i++) {
      var proto = Object.getPrototypeOf(els[i]);
      if (proto) {
        patchPrototype(proto);
        patched = true;
      }
      rewireInstance(els[i]);
    }
    return patched;
  }

  // Done when every cart-drawer on the page is rewired, or there are none.
  function patchComplete() {
    var els = document.querySelectorAll('cart-drawer');
    if (els.length === 0) {
      // No drawer yet/at all: prototype patch is enough for future instances.
      return !!(window.customElements && window.customElements.get('cart-drawer'));
    }
    for (var i = 0; i < els.length; i++) {
      if (!els[i].__hsRewired) return false;
    }
    return true;
  }

  // The custom element may upgrade before or after us; keep trying briefly so
  // we both patch the prototype and rewire any already-constructed instance.
  if (window.customElements && customElements.whenDefined) {
    customElements.whenDefined('cart-drawer').then(tryPatch);
  }
  tryPatch();
  if (!patchComplete()) {
    var tries = 0;
    var iv = setInterval(function () {
      tryPatch();
      if (patchComplete() || ++tries > 60) clearInterval(iv);
    }, 50);
  }

  /* ------------------------------------------------------------------ *
   * 5. INITIAL PAINT  —  apply IG prices once on load (replacing the
   *    deleted inline observer), and seed the running cart total.
   * ------------------------------------------------------------------ */
  function boot() {
    var drawer = document.getElementById('cart-drawer');
    if (drawer) stampKeys(drawer);
    // Seed total from the inline snippet if present, else from /cart.js.
    if (window.__hsCartTotal == null && typeof window.__hsBootCartTotal === 'number') {
      window.__hsCartTotal = window.__hsBootCartTotal;
    }
    applyIgPrices(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();